import { useState } from "react";

export interface UserPerforation {
  id: string;
  top_ft: number | null;
  bottom_ft: number | null;
}

interface PerforationEntryFormProps {
  perforations: UserPerforation[];
  onAdd: (perforation: UserPerforation) => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, field: keyof UserPerforation, value: number | null) => void;
}

export default function PerforationEntryForm({
  perforations,
  onAdd,
  onRemove,
  onUpdate,
}: PerforationEntryFormProps) {
  const [newTopFt, setNewTopFt] = useState("");
  const [newBottomFt, setNewBottomFt] = useState("");

  const handleAdd = () => {
    if (!newTopFt || !newBottomFt) {
      alert("Please enter both top and bottom depths for the perforation interval");
      return;
    }

    const top = parseFloat(newTopFt);
    const bottom = parseFloat(newBottomFt);

    if (top >= bottom) {
      alert("Top depth must be less than bottom depth");
      return;
    }

    const newPerforation: UserPerforation = {
      id: `user_perf_${Date.now()}`,
      top_ft: top,
      bottom_ft: bottom,
    };

    onAdd(newPerforation);

    // Reset form
    setNewTopFt("");
    setNewBottomFt("");
  };

  return (
    <div className="bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
        <h4 className="text-sm font-bold text-purple-900 dark:text-purple-100">
          Perforations / Producing Intervals (Manual Entry Required)
        </h4>
      </div>
      <p className="text-xs text-purple-800 dark:text-purple-200 mb-4">
        NM wells require manual perforation entry. Add each perforated interval in the well.
      </p>

      {/* Existing Perforations */}
      {perforations.length > 0 && (
        <div className="space-y-3 mb-4">
          <h5 className="text-xs font-bold text-purple-900 dark:text-purple-100 uppercase">
            Added Perforation Intervals
          </h5>
          {perforations.map((perf, index) => (
            <div key={perf.id} className="bg-white dark:bg-gray-800 border-2 border-brand-300 dark:border-brand-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-800 dark:text-white/90">
                    Perforation Interval #{index + 1}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                    {perf.top_ft !== null && perf.bottom_ft !== null
                      ? `${perf.top_ft.toLocaleString()} - ${perf.bottom_ft.toLocaleString()} ft`
                      : "Depths not set"}
                  </p>
                  <p className="text-xs text-brand-600 dark:text-brand-400 font-medium mt-1">
                    User Added
                  </p>
                </div>
                <button
                  onClick={() => onRemove(perf.id)}
                  className="ml-3 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title="Remove perforation"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Top (ft)
                  </label>
                  <input
                    type="number"
                    value={perf.top_ft !== null ? perf.top_ft : ""}
                    onChange={(e) => onUpdate(perf.id, "top_ft", e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="e.g., 7000"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Bottom (ft)
                  </label>
                  <input
                    type="number"
                    value={perf.bottom_ft !== null ? perf.bottom_ft : ""}
                    onChange={(e) => onUpdate(perf.id, "bottom_ft", e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="e.g., 7500"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add New Perforation Form */}
      <div className="pt-4 border-t border-purple-300 dark:border-purple-700">
        <h5 className="text-xs font-bold text-purple-900 dark:text-purple-100 uppercase mb-3">
          Add Perforation Interval
        </h5>
        <div className="bg-white dark:bg-gray-800 border-2 border-dashed border-purple-300 dark:border-purple-700 rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Top Depth (ft MD)
              </label>
              <input
                type="number"
                value={newTopFt}
                onChange={(e) => setNewTopFt(e.target.value)}
                placeholder="e.g., 7000"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Bottom Depth (ft MD)
              </label>
              <input
                type="number"
                value={newBottomFt}
                onChange={(e) => setNewBottomFt(e.target.value)}
                placeholder="e.g., 7500"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
          </div>
          <button
            onClick={handleAdd}
            className="w-full px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Perforation Interval
          </button>
        </div>
      </div>

      {/* Empty State */}
      {perforations.length === 0 && (
        <div className="text-center py-4 text-purple-700 dark:text-purple-300">
          <p className="text-sm">No perforation intervals added yet.</p>
          <p className="text-xs mt-1">Add perforations above if the well has been perforated.</p>
        </div>
      )}
    </div>
  );
}
